/**
 * Routing Service - OSRM + ORS with caching
 *
 * Uses OSRM as primary service (fast, free) for simple routes
 * Falls back to ORS for complex routes, wheelchair routing, or when OSRM fails
 *
 * Supported transport modes:
 * - driving (OSRM + ORS)
 * - cycling (OSRM + ORS)
 * - walking (OSRM + ORS)
 * - wheelchair (ORS only)
 */

// ============================================
// TYPES
// ============================================

export type TransportMode = 'driving' | 'cycling' | 'walking' | 'wheelchair';

export type RouteMarkerType = 'gps' | 'friend' | 'userAddress' | 'place';

export interface RoutePoint {
  lat: number;
  lng: number;
  name?: string;
  address?: string;
  // Marker rendering info - stored at creation time
  markerType?: RouteMarkerType; // Type of marker for styling
  profilePhoto?: string;        // Photo URL for friend/user markers
  firstName?: string;           // For generating initials if no photo
  lastName?: string;
  friendId?: number;            // ID if this is a friend's location (for hiding duplicate markers)
}

export interface RouteStep {
  instruction: string;
  distance: number; // meters
  duration: number; // seconds
  name?: string;
  maneuver?: {
    type: string;
    modifier?: string;
    bearing_before?: number;
    bearing_after?: number;
  };
}

export interface Route {
  id: string;
  geometry: [number, number][]; // Array of [lat, lng] for polyline
  distance: number; // meters
  duration: number; // seconds
  steps: RouteStep[];
  summary?: string;
  isAlternative?: boolean;
}

export interface RoutingResult {
  routes: Route[];
  origin: RoutePoint;
  destination: RoutePoint;
  waypoints: RoutePoint[];
  mode: TransportMode;
  provider: 'graphhopper' | 'osrm' | 'ors';
  cachedAt?: number;
}

export interface RoutingOptions {
  mode: TransportMode;
  alternatives?: boolean; // Request alternative routes
  steps?: boolean; // Include turn-by-turn instructions
  overview?: 'full' | 'simplified' | 'false';
  language?: 'fr' | 'en';
}

export interface RoutingError {
  code: 'TIMEOUT' | 'DISTANCE_LIMIT' | 'NO_ROUTE' | 'NETWORK_ERROR' | 'UNKNOWN';
  message: string;
  provider?: 'osrm' | 'ors';
  suggestGoogleMaps?: boolean;
}

// ============================================
// CACHE CONFIGURATION
// ============================================

const CACHE_KEY_PREFIX = 'route_cache_';
const CACHE_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const MAX_CACHE_ENTRIES = 50;
const ORS_MAX_DISTANCE_KM = 150; // ORS free tier limit

// ============================================
// API USAGE TRACKING
// ============================================

const USAGE_KEY = 'routing_api_usage';

interface DailyUsage {
  date: string;
  osrm: number;
  graphhopper: number;
  ors: number;
}

function getTodayKey(): string {
  return new Date().toISOString().split('T')[0]; // YYYY-MM-DD
}

function getUsage(): DailyUsage {
  try {
    const stored = localStorage.getItem(USAGE_KEY);
    if (stored) {
      const usage = JSON.parse(stored) as DailyUsage;
      // Reset if it's a new day
      if (usage.date !== getTodayKey()) {
        return { date: getTodayKey(), osrm: 0, graphhopper: 0, ors: 0 };
      }
      return usage;
    }
  } catch {}
  return { date: getTodayKey(), osrm: 0, graphhopper: 0, ors: 0 };
}

function trackUsage(provider: 'osrm' | 'graphhopper' | 'ors'): void {
  const usage = getUsage();
  usage[provider]++;
  localStorage.setItem(USAGE_KEY, JSON.stringify(usage));

  // Log warnings when approaching limits
  if (provider === 'graphhopper' && usage.graphhopper >= 400) {
    console.warn(`⚠️ GraphHopper: ${usage.graphhopper}/500 requêtes aujourd'hui`);
  }
  if (provider === 'ors' && usage.ors >= 1600) {
    console.warn(`⚠️ ORS: ${usage.ors}/2000 requêtes aujourd'hui`);
  }
}

/** Get current API usage stats - call from console: getRoutingUsage() */
export function getRoutingUsage(): DailyUsage {
  const usage = getUsage();
  console.log(`📊 Usage API Routing (${usage.date}):`);
  console.log(`   OSRM: ${usage.osrm} (illimité)`);
  console.log(`   GraphHopper: ${usage.graphhopper}/500`);
  console.log(`   ORS: ${usage.ors}/2000`);
  return usage;
}

// Expose to browser console for easy access
if (typeof window !== 'undefined') {
  (window as any).getRoutingUsage = getRoutingUsage;
}

// Calculate straight-line distance between two points (Haversine formula)
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Estimate if route distance might exceed ORS limit
function estimateRouteDistance(origin: RoutePoint, destination: RoutePoint, waypoints: RoutePoint[]): number {
  const points = [origin, ...waypoints, destination];
  let totalDistance = 0;
  for (let i = 0; i < points.length - 1; i++) {
    totalDistance += haversineDistance(
      Number(points[i].lat), Number(points[i].lng),
      Number(points[i + 1].lat), Number(points[i + 1].lng)
    );
  }
  // Multiply by 1.3 to account for road routing being longer than straight line
  return totalDistance * 1.3;
}

interface CacheEntry {
  result: RoutingResult;
  timestamp: number;
}

// ============================================
// BACKEND PROXY FOR ROUTING
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Timeout strategy: OSRM fast timeout, then fallback
const OSRM_FAST_TIMEOUT_MS = 2000; // 2 seconds - if OSRM is slow, fallback quickly
const OSRM_WAYPOINTS_TIMEOUT_MS = 4000; // 4 seconds when waypoints present (more complex calculation)
const GRAPHHOPPER_TIMEOUT_MS = 15000; // 15 seconds for GraphHopper (last resort)

// Map our transport modes to GraphHopper profiles
const GRAPHHOPPER_PROFILES: Record<TransportMode, string | null> = {
  driving: 'car',
  cycling: 'bike',
  walking: 'foot',
  wheelchair: null, // GraphHopper doesn't support wheelchair in free tier
};

// Map our transport modes to OSRM profiles
// OSRM public server profiles: car, bike, foot
const OSRM_PROFILES: Record<TransportMode, string | null> = {
  driving: 'car',
  cycling: 'bike',
  walking: 'foot',
  wheelchair: null, // OSRM doesn't support wheelchair
};

// ============================================
// GRAPHHOPPER SERVICE (Primary)
// ============================================

async function fetchGraphHopperRoute(
  origin: RoutePoint,
  destination: RoutePoint,
  waypoints: RoutePoint[],
  options: RoutingOptions
): Promise<RoutingResult | null> {
  const profile = GRAPHHOPPER_PROFILES[options.mode];

  if (!profile) {
    console.log(`GraphHopper does not support ${options.mode} mode`);
    return null;
  }

  // Build points array: "lat,lng" format for each point
  const allPoints = [origin, ...waypoints, destination];

  // GraphHopper doesn't support alternatives with waypoints
  const supportsAlternatives = waypoints.length === 0;

  const params = new URLSearchParams({
    locale: options.language || 'fr',
    instructions: options.steps !== false ? 'true' : 'false',
    alternatives: (options.alternatives && supportsAlternatives) ? 'true' : 'false',
  });

  // Add each point as 'points' parameter (backend expects array)
  allPoints.forEach(p => {
    params.append('points', `${Number(p.lat)},${Number(p.lng)}`);
  });

  // Use backend proxy
  const url = `${API_BASE_URL}/routing/graphhopper/${profile}?${params}`;

  try {

    // Add timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GRAPHHOPPER_TIMEOUT_MS);

    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn('GraphHopper request failed:', response.status, errorData);
      return null;
    }

    trackUsage('graphhopper');
    const data = await response.json();

    if (!data.paths?.length) {
      console.warn('GraphHopper returned no routes');
      return null;
    }

    // Transform GraphHopper response to our format
    const routes: Route[] = data.paths.map((path: any, index: number) => ({
      id: `gh-${index}`,
      geometry: path.points.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]), // [lng, lat] -> [lat, lng]
      distance: path.distance,
      duration: path.time / 1000, // GraphHopper returns time in milliseconds
      steps: path.instructions?.map((instr: any) => ({
        instruction: instr.text || '',
        distance: instr.distance,
        duration: instr.time / 1000,
        name: instr.street_name,
        maneuver: {
          type: getGraphHopperManeuverType(instr.sign),
          modifier: getGraphHopperManeuverModifier(instr.sign),
        },
      })) || [],
      summary: path.description?.join(' - ') || '',
      isAlternative: index > 0,
    }));

    return {
      routes,
      origin,
      destination,
      waypoints,
      mode: options.mode,
      provider: 'graphhopper',
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn('GraphHopper request timeout');
    } else {
      console.error('GraphHopper fetch error:', error);
    }
    return null;
  }
}

// Helper to convert GraphHopper sign to maneuver type
function getGraphHopperManeuverType(sign: number): string {
  const types: { [key: number]: string } = {
    '-98': 'uturn',
    '-8': 'uturn',
    '-7': 'keep',
    '-6': 'off-ramp',
    '-3': 'turn',
    '-2': 'turn',
    '-1': 'turn',
    '0': 'continue',
    '1': 'turn',
    '2': 'turn',
    '3': 'turn',
    '4': 'depart',
    '5': 'arrive',
    '6': 'roundabout',
    '7': 'keep',
    '8': 'uturn',
  };
  return types[sign] || 'turn';
}

// Helper to convert GraphHopper sign to maneuver modifier
function getGraphHopperManeuverModifier(sign: number): string | undefined {
  const modifiers: { [key: number]: string | undefined } = {
    '-3': 'sharp left',
    '-2': 'left',
    '-1': 'slight left',
    '0': 'straight',
    '1': 'slight right',
    '2': 'right',
    '3': 'sharp right',
    '-7': 'left',
    '7': 'right',
  };
  return modifiers[sign];
}

// ============================================
// OSRM SERVICE (Fallback)
// ============================================

async function fetchOSRMRoute(
  origin: RoutePoint,
  destination: RoutePoint,
  waypoints: RoutePoint[],
  options: RoutingOptions
): Promise<RoutingResult | null> {
  const profile = OSRM_PROFILES[options.mode];

  if (!profile) {
    console.log(`OSRM does not support ${options.mode} mode`);
    return null;
  }

  // Build coordinates string: origin;waypoint1;waypoint2;...;destination
  const allPoints = [origin, ...waypoints, destination];
  const coordsString = allPoints
    .map(p => `${Number(p.lng)},${Number(p.lat)}`)
    .join(';');

  const params = new URLSearchParams({
    coordinates: coordsString,
    overview: options.overview || 'full',
    steps: options.steps !== false ? 'true' : 'false',
    alternatives: options.alternatives ? 'true' : 'false',
  });

  // Use backend proxy to avoid CORS issues
  const url = `${API_BASE_URL}/routing/osrm/${profile}?${params}`;

  try {
    // Use longer timeout when waypoints are present (more complex calculation)
    const timeoutMs = waypoints.length > 0 ? OSRM_WAYPOINTS_TIMEOUT_MS : OSRM_FAST_TIMEOUT_MS;

    // Add timeout - if OSRM is slow, fallback to other services
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn('OSRM request failed:', response.status, errorData);
      return null;
    }

    trackUsage('osrm');
    const data = await response.json();

    if (data.code !== 'Ok' || !data.routes?.length) {
      console.warn('OSRM returned no routes:', data.code);
      return null;
    }

    // Transform OSRM response to our format
    const routes: Route[] = data.routes.map((route: any, index: number) => ({
      id: `osrm-${index}`,
      geometry: route.geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]), // GeoJSON is [lng, lat], we need [lat, lng]
      distance: route.distance,
      duration: route.duration,
      steps: route.legs?.flatMap((leg: any) =>
        leg.steps?.map((step: any) => ({
          instruction: step.name || step.maneuver?.type || '',
          distance: step.distance,
          duration: step.duration,
          name: step.name,
          maneuver: step.maneuver ? {
            type: step.maneuver.type,
            modifier: step.maneuver.modifier,
            bearing_before: step.maneuver.bearing_before,
            bearing_after: step.maneuver.bearing_after,
          } : undefined,
        })) || []
      ) || [],
      summary: route.legs?.map((leg: any) => leg.summary).join(' - '),
      isAlternative: index > 0,
    }));

    return {
      routes,
      origin,
      destination,
      waypoints,
      mode: options.mode,
      provider: 'osrm',
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      const timeoutSec = waypoints.length > 0 ? '4s' : '2s';
      console.log(`OSRM timeout (${timeoutSec}) - falling back to other services`);
    } else {
      console.warn('OSRM fetch error:', error);
    }
    return null;
  }
}

// ============================================
// ORS SERVICE (Fallback) - Via Backend Proxy
// ============================================

// Map our transport modes to ORS profiles
const ORS_PROFILES: Record<TransportMode, string> = {
  driving: 'driving-car',
  cycling: 'cycling-regular',
  walking: 'foot-walking',
  wheelchair: 'wheelchair',
};

async function fetchORSRoute(
  origin: RoutePoint,
  destination: RoutePoint,
  waypoints: RoutePoint[],
  options: RoutingOptions
): Promise<RoutingResult | null> {
  const profile = ORS_PROFILES[options.mode];

  // Build coordinates array: [[lng, lat], ...]
  const allPoints = [origin, ...waypoints, destination];
  const coordinates = allPoints.map(p => [Number(p.lng), Number(p.lat)]);

  // ORS doesn't support alternatives with waypoints (more than 2 points)
  const supportsAlternatives = waypoints.length === 0;

  const requestBody = {
    coordinates,
    alternatives: (options.alternatives && supportsAlternatives) || false,
    language: options.language || 'fr',
  };

  // Use backend proxy to avoid CORS issues
  const url = `${API_BASE_URL}/routing/ors/${profile}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('ORS request failed:', response.status, errorText);
      return null;
    }

    trackUsage('ors');
    const data = await response.json();

    if (!data.features?.length) {
      console.warn('ORS returned no routes');
      return null;
    }

    // Transform ORS GeoJSON response to our format
    const routes: Route[] = data.features.map((feature: any, index: number) => {
      const props = feature.properties;
      const segments = props.segments || [];

      return {
        id: `ors-${index}`,
        geometry: feature.geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]), // GeoJSON is [lng, lat], we need [lat, lng]
        distance: props.summary?.distance || 0,
        duration: props.summary?.duration || 0,
        steps: segments.flatMap((segment: any) =>
          segment.steps?.map((step: any) => ({
            instruction: step.instruction || '',
            distance: step.distance,
            duration: step.duration,
            name: step.name,
            maneuver: step.type ? {
              type: getManeuverType(step.type),
              modifier: getManeuverModifier(step.type),
            } : undefined,
          })) || []
        ),
        summary: segments.map((s: any) => s.steps?.[0]?.name).filter(Boolean).join(' - '),
        isAlternative: index > 0,
      };
    });

    return {
      routes,
      origin,
      destination,
      waypoints,
      mode: options.mode,
      provider: 'ors',
    };
  } catch (error) {
    console.error('ORS fetch error:', error);
    return null;
  }
}

// Helper to convert ORS step type to maneuver type
function getManeuverType(orsType: number): string {
  const types: Record<number, string> = {
    0: 'turn',
    1: 'turn',
    2: 'turn',
    3: 'turn',
    4: 'continue',
    5: 'roundabout',
    6: 'depart',
    7: 'arrive',
    8: 'via',
    9: 'exit',
    10: 'merge',
    11: 'on-ramp',
    12: 'off-ramp',
    13: 'fork',
  };
  return types[orsType] || 'turn';
}

// Helper to convert ORS step type to maneuver modifier
function getManeuverModifier(orsType: number): string | undefined {
  const modifiers: Record<number, string> = {
    0: 'left',
    1: 'right',
    2: 'sharp left',
    3: 'sharp right',
    4: 'straight',
    5: undefined as any, // roundabout
    6: undefined as any, // depart
    7: undefined as any, // arrive
  };
  return modifiers[orsType];
}

// ============================================
// CACHE MANAGEMENT
// ============================================

function getCacheKey(
  origin: RoutePoint,
  destination: RoutePoint,
  waypoints: RoutePoint[],
  mode: TransportMode
): string {
  const pointsHash = [origin, ...waypoints, destination]
    .map(p => `${Number(p.lat).toFixed(5)},${Number(p.lng).toFixed(5)}`)
    .join('|');
  return `${CACHE_KEY_PREFIX}${mode}_${pointsHash}`;
}

function getFromCache(key: string): RoutingResult | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const entry: CacheEntry = JSON.parse(cached);
    const now = Date.now();

    if (now - entry.timestamp > CACHE_DURATION_MS) {
      localStorage.removeItem(key);
      return null;
    }

    return {
      ...entry.result,
      cachedAt: entry.timestamp,
    };
  } catch {
    return null;
  }
}

function saveToCache(key: string, result: RoutingResult): void {
  try {
    // Clean old cache entries if we have too many
    cleanOldCacheEntries();

    const entry: CacheEntry = {
      result,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    // Handle quota exceeded error by clearing all route cache entries
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.log('localStorage quota exceeded, clearing route cache...');
      clearAllRouteCache();
      // Try one more time after clearing
      try {
        const entry: CacheEntry = {
          result,
          timestamp: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(entry));
      } catch {
        console.warn('Failed to save route to cache even after clearing');
      }
    } else {
      console.warn('Failed to save route to cache:', error);
    }
  }
}

function clearAllRouteCache(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
    console.log(`Cleared ${keys.length} route cache entries`);
  } catch (error) {
    console.warn('Failed to clear route cache:', error);
  }
}

function cleanOldCacheEntries(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));

    if (keys.length >= MAX_CACHE_ENTRIES) {
      // Remove oldest entries
      const entries = keys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}');
          return { key, timestamp: data.timestamp || 0 };
        } catch {
          return { key, timestamp: 0 };
        }
      });

      entries.sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 10 entries
      entries.slice(0, 10).forEach(entry => {
        localStorage.removeItem(entry.key);
      });
    }
  } catch {
    // Ignore cache cleanup errors
  }
}

export function clearRouteCache(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore errors
  }
}

// ============================================
// MAIN ROUTING FUNCTION
// ============================================

export interface CalculateRouteResult {
  result: RoutingResult | null;
  error: RoutingError | null;
}

/**
 * Calculate a route between two points with optional waypoints
 *
 * Strategy (optimized for cost):
 * 1. OSRM (free, unlimited) with fast 2s timeout
 * 2. ORS (free, 2000/day) for distances < 150km
 * 3. GraphHopper (free, 500/day but ~3 req per search with alternatives) as last resort
 *
 * Returns both the result and any error information
 */
export async function calculateRoute(
  origin: RoutePoint,
  destination: RoutePoint,
  waypoints: RoutePoint[] = [],
  options: RoutingOptions = { mode: 'driving' }
): Promise<CalculateRouteResult> {
  // Check cache first
  const cacheKey = getCacheKey(origin, destination, waypoints, options.mode);
  const cached = getFromCache(cacheKey);

  if (cached) {
    // Validate that cached mode matches requested mode
    if (cached.mode !== options.mode) {
      localStorage.removeItem(cacheKey);
    } else {
      return { result: cached, error: null };
    }
  }

  // Estimate distance for fallback decisions
  const estimatedDistanceKm = estimateRouteDistance(origin, destination, waypoints);
  const canUseOrs = estimatedDistanceKm <= ORS_MAX_DISTANCE_KM;

  // For wheelchair mode, use ORS directly (OSRM/GraphHopper don't support it)
  if (options.mode === 'wheelchair') {
    if (!canUseOrs) {
      return {
        result: null,
        error: {
          code: 'DISTANCE_LIMIT',
          message: `La distance estimée (${Math.round(estimatedDistanceKm)}km) dépasse la limite de ${ORS_MAX_DISTANCE_KM}km pour le mode fauteuil roulant.`,
          provider: 'ors',
          suggestGoogleMaps: true,
        },
      };
    }
    const result = await fetchORSRoute(origin, destination, waypoints, options);
    if (result) {
      saveToCache(cacheKey, result);
      return { result, error: null };
    }
    return {
      result: null,
      error: {
        code: 'NO_ROUTE',
        message: 'Impossible de calculer un itinéraire accessible.',
        provider: 'ors',
        suggestGoogleMaps: true,
      },
    };
  }

  // === STRATEGY ===
  // OSRM public server only supports 'car' profile!
  // For other modes, use GraphHopper or ORS directly.

  let result: RoutingResult | null = null;

  if (options.mode === 'driving') {
    // For driving: Try OSRM first (free, unlimited)
    result = await fetchOSRMRoute(origin, destination, waypoints, options);

    // Fallback to ORS if OSRM fails
    if (!result && canUseOrs) {
      result = await fetchORSRoute(origin, destination, waypoints, options);
    }

    // Last resort: GraphHopper
    if (!result) {
      result = await fetchGraphHopperRoute(origin, destination, waypoints, options);
    }
  } else {
    // For cycling/walking: Use GraphHopper first (supports these modes)
    result = await fetchGraphHopperRoute(origin, destination, waypoints, options);

    // Fallback to ORS if GraphHopper fails and distance is reasonable
    if (!result && canUseOrs) {
      result = await fetchORSRoute(origin, destination, waypoints, options);
    }
  }

  // Cache the result
  if (result) {
    saveToCache(cacheKey, result);
    return { result, error: null };
  }

  // All services failed - provide helpful error
  return {
    result: null,
    error: {
      code: 'NETWORK_ERROR',
      message: 'Impossible de calculer l\'itinéraire. Les serveurs de routing sont temporairement indisponibles.',
      suggestGoogleMaps: true,
    },
  };
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format distance for display
 */
export function formatDistance(meters: number, locale: 'fr' | 'en' = 'fr'): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  const km = meters / 1000;
  if (locale === 'fr') {
    return `${km.toFixed(1).replace('.', ',')} km`;
  }
  return `${km.toFixed(1)} km`;
}

/**
 * Format duration for display
 */
export function formatDuration(seconds: number, locale: 'fr' | 'en' = 'fr'): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);

  if (hours === 0) {
    return locale === 'fr' ? `${minutes} min` : `${minutes} min`;
  }

  if (minutes === 0) {
    return locale === 'fr' ? `${hours} h` : `${hours} h`;
  }

  return locale === 'fr' ? `${hours} h ${minutes} min` : `${hours} h ${minutes} min`;
}

/**
 * Generate Google Maps directions URL
 */
export function getGoogleMapsUrl(
  origin: RoutePoint,
  destination: RoutePoint,
  waypoints: RoutePoint[] = [],
  mode: TransportMode = 'driving'
): string {
  const modeMap: Record<TransportMode, string> = {
    driving: 'driving',
    cycling: 'bicycling',
    walking: 'walking',
    wheelchair: 'walking', // Google Maps doesn't have wheelchair mode
  };

  let url = `https://www.google.com/maps/dir/?api=1`;
  url += `&origin=${Number(origin.lat)},${Number(origin.lng)}`;
  url += `&destination=${Number(destination.lat)},${Number(destination.lng)}`;
  url += `&travelmode=${modeMap[mode]}`;

  if (waypoints.length > 0) {
    const waypointsStr = waypoints
      .map(w => `${Number(w.lat)},${Number(w.lng)}`)
      .join('|');
    url += `&waypoints=${encodeURIComponent(waypointsStr)}`;
  }

  return url;
}

/**
 * Generate Citymapper URL (for public transit)
 */
export function getCitymapperUrl(
  origin: RoutePoint,
  destination: RoutePoint
): string {
  return `https://citymapper.com/directions?startcoord=${Number(origin.lat)},${Number(origin.lng)}&endcoord=${Number(destination.lat)},${Number(destination.lng)}`;
}

/**
 * Generate Waze URL
 */
export function getWazeUrl(
  destination: RoutePoint
): string {
  // Waze only needs the destination - it uses current location as origin
  return `https://waze.com/ul?ll=${Number(destination.lat)},${Number(destination.lng)}&navigate=yes`;
}

/**
 * Get transport mode icon
 */
export function getTransportModeIcon(mode: TransportMode): string {
  const icons: Record<TransportMode, string> = {
    driving: '🚗',
    cycling: '🚴',
    walking: '🚶',
    wheelchair: '♿',
  };
  return icons[mode];
}

/**
 * Get transport mode label
 */
export function getTransportModeLabel(mode: TransportMode, locale: 'fr' | 'en' = 'fr'): string {
  const labels: Record<TransportMode, Record<'fr' | 'en', string>> = {
    driving: { fr: 'Voiture', en: 'Driving' },
    cycling: { fr: 'Vélo', en: 'Cycling' },
    walking: { fr: 'À pied', en: 'Walking' },
    wheelchair: { fr: 'Fauteuil roulant', en: 'Wheelchair' },
  };
  return labels[mode][locale];
}

// ============================================
// EXPORT ALL
// ============================================

const routingService = {
  calculateRoute,
  formatDistance,
  formatDuration,
  getGoogleMapsUrl,
  getCitymapperUrl,
  getWazeUrl,
  getTransportModeIcon,
  getTransportModeLabel,
  clearRouteCache,
  haversineDistance,
};

export default routingService;
